<template>
  <div class="create-auction-form">

    <form ref="auctionForm">

      <div class="grey-box-container">
        <h2>Auction Details</h2>

        <fieldset class="col-12">
          <label for="type">Auction Type *</label>
          <div class="pfx-selectbox">
            <select id="type" v-model="auctionMetadata.type" required>
              <option v-for="type in auctionTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
            </select>
          </div>
        </fieldset>

        <div class="clearfix">
          <fieldset class="col-12">
            <label for="startingPrice">Starting Price * <span class="label-sidenote">&mdash; all prices are in US dollars ($)</span></label>
            <currency-input id="startingPrice" v-model="auction.startingPrice" required />
          </fieldset>

          <fieldset class="col-12">
            <label for="reservePrice">Reserve Price *</label>
            <currency-input id="reservePrice" v-model="auction.reservePrice" required />
            <input type="checkbox" v-model="auctionMetadata.hideReservePrice" :value="true" id="hide"> <label for="hide">Hide</label>
          </fieldset>
        </div>

        <div class="clearfix">
          <fieldset class="col-12">
            <label for="minimumIncrement">Minimum Bid Increments *</label>
            <currency-input id="minimumIncrement" v-model="auction.minimumIncrement" required />
          </fieldset>
          <fieldset class="col-12">
            <label>Allow Autobids?</label>
            <v-radio-group v-model="auctionMetadata.allowAutobids" row>
              <v-radio label="Allowed" :value="true" color="pfxBrand" />
              <v-radio label="Not allowed" :value="false" color="pfxBrand" />
            </v-radio-group>
          </fieldset>
        </div>
      </div>

      <div class="grey-box-container">
        <h2>Visibility</h2>
        <v-radio-group v-model="auction.globalVisibility">
          <v-radio label="Promote this auction also in the global PFX listings." :value="true" color="pfxBrand" />
          <v-radio label="Limit the visibility only to the your own company PFX subsite." :value="false" color="pfxBrand" />
        </v-radio-group>
      </div>

      <div class="grey-box-container" v-if="tenantRole === tenantRoles.ADMIN.value">
        <h2>Listing Agent</h2>
        <div class="clearfix">
          <fieldset class="col-12">
            <label for="listingAgent">Agent *</label>
            <div class="pfx-selectbox">
              <select v-model="auction.listingAgent" id="listingAgent">
                <option v-for="listingAgent in listingAgents" :key="listingAgent.userId" :value="listingAgent.userId">
                  {{ listingAgent.profile.firstName }} {{ listingAgent.profile.lastName }}
                </option>
              </select>
            </div>
            <small>Note that switching the listing agent is only available for the company administrators.</small>
          </fieldset>
        </div>
      </div>

      <div class="grey-box-container">
        <h2>Seller</h2>
        <div class="clearfix">
          <fieldset class="col-12" v-if="sellerProfile.id">
            <div style="font-weight: 600; font-size: 18px;">{{ sellerProfile.firstName }} {{ sellerProfile.lastName }}, {{ sellerProfile.email }}</div>
            <small>Seller has approved your invitation.</small>
          </fieldset>
          <fieldset class="col-12" v-else>
            <label for="sellerEmail">Invite Seller</label>
            <input type="email" id="sellerEmail" v-model="auction.sellerEmail" placeholder="Email address" />
            <small>Auction invitation will be sent to the provided email address. Seller must create a PFX account and accept the invitation.</small>
          </fieldset>
        </div>
      </div>

      <div class="grey-box-container">
        <h2>Auction Time Periods</h2>
        <div class="clearfix">
          <fieldset class="col-12">
            <label for="timezone">Timezone</label>
            <div class="pfx-selectbox">
              <select id="timezone" v-model="auctionMetadata.timezone" size="1" required>
                <!-- refer to https://en.wikipedia.org/wiki/List_of_tz_database_time_zones -->
                <option value="America/Los_Angeles">Pacific Time Zone (PST/PDT)</option>
                <option value="America/Denver">Mountain Time Zone (MST/MDT)</option>
                <option value="America/Chicago">Central Time Zone (CST/CDT)</option>
                <option value="America/New_York">Eastern Time Zone (EST/EDT)</option>
              </select>
            </div>
          </fieldset>
        </div>
        <h3>Publish Auction</h3>
        <p class="dim">Choose when you will publish and open your auction for Registrations and when it will close.</p>
        <div class="clearfix date-inputs">
          <!-- TODO refactor a separate component for date/time selection -->
          <fieldset class="col-12">
            <label>Publish start date *</label>
            <div>
              <v-menu class="date-icon" bottom offset-y lazy :close-on-content-click="true">
                <input class="date" type="text" readonly :value="auction.publishDate | prettyDate(auctionMetadata.timezone)" slot="activator" required />
                <v-date-picker v-model="publishDateDate" no-title />
              </v-menu>
            </div>
          </fieldset>
          <fieldset class="col-12">
            <label>Publish start time *</label>
            <div>
              <v-menu ref="menuPubTime" bottom offset-y lazy :close-on-content-click="false">
                <input class="time" type="text" readonly :value="auction.publishDate | prettyTime(auctionMetadata.timezone)" slot="activator" required />
                <v-time-picker v-model="publishDateTime">
                  <div style="width: 100%; text-align: center;">
                    <button @click="$refs.menuPubTime.save()">Close</button>
                  </div>
                </v-time-picker>
              </v-menu>
            </div>
          </fieldset>
        </div>
        <div class="clearfix date-inputs">
          <fieldset class="col-12">
            <label>Registration close date *</label>
            <div>
              <v-menu bottom offset-y lazy :close-on-content-click="true">
                <input class="date" type="text" readonly :value="auction.freezeDate | prettyDate(auctionMetadata.timezone)" slot="activator" required />
                <v-date-picker v-model="freezeDateDate" no-title />
              </v-menu>
            </div>
          </fieldset>
          <fieldset class="col-12">
            <label>Registration close time *</label>
            <div>
              <v-menu ref="menuRegTime" bottom offset-y lazy :close-on-content-click="false">
                <input class="time" type="text" readonly :value="auction.freezeDate | prettyTime(auctionMetadata.timezone)" slot="activator" required />
                <v-time-picker v-model="freezeDateTime">
                  <div style="width: 100%; text-align: center;">
                    <button @click="$refs.menuRegTime.save()">Close</button>
                  </div>
                </v-time-picker>
              </v-menu>
            </div>
          </fieldset>
        </div>
        <h3>Auction Period</h3>
        <p class="dim">Choose when you will open your auction for bids and when the auction ends.</p>
        <div class="clearfix date-inputs">
          <fieldset class="col-12">
            <label>Auction open date *</label>
            <div>
              <v-menu bottom offset-y lazy :close-on-content-click="true">
                <input class="date" type="text" readonly :value="auction.startDate | prettyDate(auctionMetadata.timezone)" slot="activator" required />
                <v-date-picker v-model="startDateDate" no-title />
              </v-menu>
            </div>
          </fieldset>
          <fieldset class="col-12">
            <label>Auction open time *</label>
            <div>
              <v-menu ref="menuOpenTime" bottom offset-y lazy :close-on-content-click="false">
                <input class="time" type="text" readonly :value="auction.startDate | prettyTime(auctionMetadata.timezone)" slot="activator" required />
                <v-time-picker v-model="startDateTime">
                  <div style="width: 100%; text-align: center;">
                    <button @click="$refs.menuOpenTime.save()">Close</button>
                  </div>
                </v-time-picker>
              </v-menu>
            </div>
          </fieldset>
        </div>
        <div class="clearfix date-inputs">
          <fieldset class="col-12">
            <label>Auction close date *</label>
            <div>
              <v-menu bottom offset-y lazy :close-on-content-click="true">
                <input class="date" type="text" readonly :value="auction.endDate | prettyDate(auctionMetadata.timezone)" slot="activator" required />
                <v-date-picker v-model="endDateDate" no-title />
              </v-menu>
            </div>
          </fieldset>
          <fieldset class="col-12">
            <label>Auction close time *</label>
            <div>
              <v-menu ref="menuEndTime" bottom offset-y lazy :close-on-content-click="false">
                <input class="time" type="text" readonly :value="auction.endDate | prettyTime(auctionMetadata.timezone)" slot="activator" required />
                <v-time-picker v-model="endDateTime">
                  <div style="width: 100%; text-align: center;">
                    <button @click="$refs.menuEndTime.save()">Close</button>
                  </div>
                </v-time-picker>
              </v-menu>
            </div>
          </fieldset>
        </div>
      </div>

      <div class="grey-box-container">
        <h2>Auction Information</h2>
        <fieldset>
          <label for="auctionInfo">Additional Information <small>&mdash; let your buyers know about events, open house, viewings and other details.</small></label>
          <v-icon class="pointer" @click="showMarkdown = !showMarkdown">{{ showMarkdown ? 'visibility_off' : 'visibility' }}</v-icon>
          <template v-if="!showMarkdown">
            <textarea id="auctionInfo" v-model="auctionMetadata.auctionInfo" rows="7"></textarea>
          </template>
          <template v-else>
            <div style="padding:0.5rem; border:1px solid #ccc; background: #ddd" class="list-bullets">
              <vue-markdown :source="auctionMetadata.auctionInfo" />
            </div>
          </template>
        </fieldset>
        <fieldset>
          <label for="commissionInfo">Please clarify the commission situation to the Agents and Buyers * <v-icon class="pointer" small v-tooltip.right="{ trigger: 'hover click', content: 'Let Selling Agents know how much their commission would be if they refer a buyer to participate in this auction.' }">info_outline</v-icon></label>
          <input type="text" id="commissionInfo" v-model="auctionMetadata.commissionInfo" required />
        </fieldset>
      </div>

      <div class="grey-box-container">
        <h2>Document Vault</h2>
        <p>
          Upload the documents to the Document Vault. Choose the level of security from Public (anyone can view),
          Logged-in (only logged-in users can view) or Authorized (only authorized bidders can view).
        </p>
        <fieldset>
          <CreateListingFileUpload v-if="!isAuctionEdit" :addFiles="true" />
          <FileBox :auctionId="auction.id" usecase="auction" v-else />
        </fieldset>

      </div>

    </form>

  </div>
</template>

<script>
  import VueMarkdown from 'vue-markdown'
  import CreateListingFileUpload from './CreateListingFileUpload.vue'
  import FileBox from './elements/FileBox.vue'
  import CurrencyInput from './elements/CurrencyInput.vue'
  import { auctionTypes, tenantRoles } from '../utils/PfxUtils.js'
  import { mapGetters } from 'vuex'
  const moment = require('moment-timezone')

  export default {
    name: 'CreateListingAuction',
    components: {
      VueMarkdown,
      CreateListingFileUpload,
      FileBox,
      CurrencyInput,
    },
    data() {
      return {
        auctionTypes,
        tenantRoles,
        auction: {
          publishDate: moment().valueOf(),
          freezeDate: 0,
          startDate: 0,
          endDate: 0,
          startingPrice: undefined,
          reservePrice: undefined,
          minimumIncrement: undefined,
          globalVisibility: true,
          sellerEmail: ''
        },
        auctionMetadata: {
          timezone: undefined,
          auctionInfo: '',
          allowAutobids: undefined
        },
        showMarkdown: false,
        listingAgents: [],
        sellerProfile: {}
      }
    },
    computed: {
      ...mapGetters([
        'isAuctionEdit',
        'tenantRole',
        'userId'
      ]),
      publishDateDate: {
        get() {
          return moment(this.auction.publishDate).tz(this.auctionMetadata.timezone).format('YYYY-MM-DD')
        },
        set(value) {
          const a = moment(value, 'YYYY-MM-DD')
          const b = moment(this.auction.publishDate).tz(this.auctionMetadata.timezone).year(a.year()).month(a.month()).date(a.date())
          this.auction.publishDate = b.valueOf()
        }
      },
      publishDateTime: {
        get() {
          return moment(this.auction.publishDate).tz(this.auctionMetadata.timezone).format('hh:mma')
        },
        set(value) {
          const a = moment(value, 'hh:mma')
          const b = moment(this.auction.publishDate).tz(this.auctionMetadata.timezone).hour(a.hour()).minute(a.minute())
          this.auction.publishDate = b.valueOf()
        }
      },
      freezeDateDate: {
        get() {
          return moment(this.auction.freezeDate).tz(this.auctionMetadata.timezone).format('YYYY-MM-DD')
        },
        set(value) {
          const a = moment(value, 'YYYY-MM-DD')
          const b = moment(this.auction.freezeDate).tz(this.auctionMetadata.timezone).year(a.year()).month(a.month()).date(a.date())
          this.auction.freezeDate = b.valueOf()
        }
      },
      freezeDateTime: {
        get() {
          return moment(this.auction.freezeDate).tz(this.auctionMetadata.timezone).format('hh:mma')
        },
        set(value) {
          const a = moment(value, 'hh:mma')
          const b = moment(this.auction.freezeDate).tz(this.auctionMetadata.timezone).hour(a.hour()).minute(a.minute())
          this.auction.freezeDate = b.valueOf()
        }
      },
      startDateDate: {
        get() {
          return moment(this.auction.startDate).tz(this.auctionMetadata.timezone).format('YYYY-MM-DD')
        },
        set(value) {
          const a = moment(value, 'YYYY-MM-DD')
          const b = moment(this.auction.startDate).tz(this.auctionMetadata.timezone).year(a.year()).month(a.month()).date(a.date())
          this.auction.startDate = b.valueOf()
        }
      },
      startDateTime: {
        get() {
          return moment(this.auction.startDate).tz(this.auctionMetadata.timezone).format('hh:mma')
        },
        set(value) {
          const a = moment(value, 'hh:mma')
          const b = moment(this.auction.startDate).tz(this.auctionMetadata.timezone).hour(a.hour()).minute(a.minute())
          this.auction.startDate = b.valueOf()
        }
      },
      endDateDate: {
        get() {
          return moment(this.auction.endDate).tz(this.auctionMetadata.timezone).format('YYYY-MM-DD')
        },
        set(value) {
          const a = moment(value, 'YYYY-MM-DD')
          const b = moment(this.auction.endDate).tz(this.auctionMetadata.timezone).year(a.year()).month(a.month()).date(a.date())
          this.auction.endDate = b.valueOf()
        }
      },
      endDateTime: {
        get() {
          return moment(this.auction.endDate).tz(this.auctionMetadata.timezone).format('hh:mma')
        },
        set(value) {
          const a = moment(value, 'hh:mma')
          const b = moment(this.auction.endDate).tz(this.auctionMetadata.timezone).hour(a.hour()).minute(a.minute())
          this.auction.endDate = b.valueOf()
        }
      }
    },
    watch: {
      auction: {
        deep: true,
        handler() {
          this.$store.commit('setDataToNewAuctionStorage', { auction: this.auction })
        }
      },
      auctionMetadata: {
        deep: true,
        handler() {
          this.$store.commit('setDataToNewAuctionStorage', { auctionMetadata: this.auctionMetadata })
        }
      }
    },
    created() {
      Object.assign(this.auction, this.$store.state.newAuctionStorage.auction)
      Object.assign(this.auctionMetadata, this.$store.state.newAuctionStorage.auctionMetadata)
      this.sellerProfile = this.$store.state.newAuctionStorage.sellerProfile

      // upon first time auction is edited, initialize with some feasible date values
      if (!this.auction.startDate && !this.auction.endDate) {
        this.auction.publishDate = moment().second(0).millisecond(0).valueOf()
        this.auction.startDate = moment().add(4, 'week').minute(0).second(0).millisecond(0).valueOf()
        this.auction.freezeDate = moment(this.auction.startDate).subtract(2, 'day').valueOf()
        this.auction.endDate = moment(this.auction.startDate).add(1, 'week').valueOf()
      }
      if (!this.auctionMetadata.timezone) {
        this.auctionMetadata.timezone = 'America/Los_Angeles'
      }
      if (!this.auction.listingAgent) {
        this.auction.listingAgent = this.userId
      }
      if (!this.auctionMetadata.type) {
        this.auctionMetadata.type = 'MINIMUM_BID'
      }

      this.$store.commit('setDataToNewAuctionStorage', { auction: this.auction })
      this.$store.commit('setDataToNewAuctionStorage', { auctionMetadata: this.auctionMetadata })

      this.loadListingAgents()
    },
    methods: {
      loadListingAgents() {
        if (this.tenantRole === tenantRoles.ADMIN.value) {
          this.$PfxBackendApi.getTenantMembers().then(res => {
            console.log('getTenantMembers res', res)
            this.listingAgents = res.data || []
          }).catch(err => {
            console.error('getTenantMembers err', err)
            this.$notify({type: 'danger', message: 'Cannot fetch listing agents'})
          })
        }
      },

      beforeNextStep() {
        if ( this.auction.publishDate >= this.auction.freezeDate ) {
          this.$notify({type: 'danger',message: 'Registration closing date must be after publish date'})
          return false
        }
        if ( this.auction.freezeDate > this.auction.startDate ) {
          this.$notify({type: 'danger',message: 'Registration closing date cannot be after start date'})
          return false
        }
        if ( this.auction.startDate >= this.auction.endDate ) {
          this.$notify({type: 'danger',message: 'Start date must be before end date'})
          return false
        }

        document.getElementById('reservePrice').setCustomValidity((this.auction.reservePrice != 0 && this.auction.reservePrice < this.auction.startingPrice) ? 'Reserve price cannot be below starting price' : '')

        if (!this.$refs.auctionForm.checkValidity()) {
          this.$refs.auctionForm.reportValidity()
          return false
        }
        return true
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import 'node_modules/jeet/scss/index';
  input.pfx-datepicker{
    padding: 1rem;
  }
  .col-13 { @include column(1/3); }
  .col-23 { @include column(2/3); }
  .col-12 { @include column(1/2); }
  .menu:after{
    content: "";
    position: absolute;
    right: 10px;
    top: 40%;
    background-image: url('../assets/select-arrow.png');
    background-size: contain;
    background-repeat: no-repeat;
    width: 15px;
    height: 20px;

  }

  label {
    font-weight: 600;
  }

  h3 {
    margin-bottom: 0;
  }
  .dim {
    color: #888;
  }

  .input-group { padding: 0; }
  .menu{
    width: 100%;
  }

  .date-inputs {
    input.date {
      width: 100%;
      // appearance: menulist;
    }
    input.time {
      width: 100%;
      // appearance: menulist;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/CreateListingAuction.vue